import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  Popover,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  User,
  Mail,
  MoreVertical,
  Trash2,
  AlertTriangle,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Users,
  UserCog,
  FileText,
  Globe,
  Folder,
  ListTodo,
  NotebookPen,
  Image as ImageIcon,
  Pencil,
  Plus,
  X,
  Archive,
  ArchiveRestore,
  Repeat,
} from "lucide-react";
import BarChart from "@mui/icons-material/BarChart";
import { GoogleDriveIcon } from "../../components/icons/GoogleDriveIcon";
import { SetupIcon } from "../../components/icons/SetupIcon";

import { Card, CardContent } from "../../components/Card";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { SetupProgress } from "../../components/SetupProgress";
import { Label } from "../../components/Label";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import { CustomSelector } from "../../components/CustomSelector";
import { Checkbox } from "../../components/Checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/DropdownMenu";
import { statusLabels } from "../../components/data/mockData";
import { mockDataConfig, brand } from "../../theme/theme";
import { useAuth } from "../../components/contexts/AuthContext";
import apiClient from "../../api-client";
import { getData } from "../../components/hooks/getData";
import { useOutletContext, useNavigate } from "react-router-dom";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import { MembersModal } from "../../components/MembersModal";
import { RecurringProjectConfigModal } from "../../components/Projects_components/RecurringProjectConfigModal";
import { usePasteSingleImage } from "../../components/hooks/usePasteImage";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { FEATURE_RECURRING_PROJECTS, canSeeContractedPack, hasAgencyFeature } from "@/utils/agencyFeatures";
import { isClientReadOnly } from "@/utils/clientAccess";

// Mock interactions data
const mockInteractions = {
  1: [
    {
      id: "int-1-1",
      type: "email",
      subject: "Bienvenida y documentación inicial",
      description: "Envío de documentación de onboarding y próximos pasos",
      date: "2024-01-16T09:00:00",
      contactPerson: "María García",
      status: "sent",
    },
    {
      id: "int-1-2",
      type: "call",
      subject: "Reunión inicial de planificación",
      description:
        "Primera llamada para definir objetivos y alcance del proyecto",
      date: "2024-01-20T10:30:00",
      contactPerson: "María García",
      status: "completed",
    },
  ],
  2: [
    {
      id: "int-2-1",
      type: "email",
      subject: "Configuración inicial e-commerce",
      description:
        "Instrucciones para la configuración inicial de la tienda online",
      date: "2024-02-21T10:00:00",
      contactPerson: "Carlos López",
      status: "sent",
    },
  ],
};

function CompactClientCardBase({
  client,
  archivedFilter = "active",
  setSelectedClient,
  onClientDeleted,
  onClientUpdated,
  availableConsultants = [], // Consultores disponibles pasados desde el componente padre
  onSuccess = () => { }, // Callback para mostrar popup de éxito
  onError = () => { },
  onViewOnboarding, // Callback para ver onboarding
  packLabels = {},
  onRecurringConfigClick,
  recurringButtonDataTutorial,
  highlightRecurringButton = false,
  recurringButtonTooltip,
  layout = "card", // "card" | "row" (fila de la vista tabla de clientes)
  columns, // columnas visibles de la tabla cuando layout === "row"
}) {
  const theme = useTheme();
  const { user: currentUser, interceptorReady } = useAuth();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const {
    selectedFilter,
    setTechnicalSetupRefreshKey,
    refreshFilterData,
    clientsRefreshKey,
  } = useOutletContext();
  const [consultantSelectorAnchor, setConsultantSelectorAnchor] =
    useState(null);
  const [localConsultantDetails, setLocalConsultantDetails] = useState(null);
  const [clientConsultantsData, setClientConsultantsData] = useState(null);
  const [loadingConsultants, setLoadingConsultants] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditPhotoModal, setShowEditPhotoModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [cropMessage, setCropMessage] = useState(null);
  const [imageMarkedForDeletion, setImageMarkedForDeletion] = useState(false);
  const fileInputRef = useRef(null);
  const menuOpen = Boolean(menuAnchorEl);
  const consultantSelectorOpen = Boolean(consultantSelectorAnchor);
  const [isOpeningConversation, setIsOpeningConversation] = useState(false);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamRefreshKey, setTeamRefreshKey] = useState(0);
  const [recurringConfigModalOpen, setRecurringConfigModalOpen] =
    useState(false);
  const [recurringConfigRefreshKey, setRecurringConfigRefreshKey] =
    useState(0);
  const navigate = useNavigate();

  // Fetch team data when modal is opened
  const { data: teamData, isLoading: loadingTeam } = getData(
    `clients/${client.id}/`,
    teamRefreshKey > 0 && interceptorReady,
    teamRefreshKey
  );

  // Extract team members from the response
  const teamMembers = React.useMemo(() => {
    if (!teamData) return { admins: [], consultants: [], clientUsers: [] };
    return {
      admins: teamData.admin_details || [],
      consultants: teamData.consultant_details || [],
      clientUsers: teamData.client_users_details || [],
    };
  }, [teamData]);

  // Banderas de rol unificadas
  const isSuperAdmin = currentUser.role === "superadmin";
  const isAgencyAdmin = currentUser.role === "agency_admin";
  const isConsultant = currentUser.role === "consultant";
  const isReadOnly = isClientReadOnly(currentUser, client);
  const showPack = canSeeContractedPack(currentUser);
  const canConfigureRecurrence =
    !isReadOnly && (isSuperAdmin || isAgencyAdmin || isConsultant) &&
    hasAgencyFeature(currentUser, FEATURE_RECURRING_PROJECTS);
  const canManageConsultants = !isReadOnly && (isSuperAdmin || isAgencyAdmin);
  const isAnyAdmin = !isReadOnly && (isSuperAdmin || isAgencyAdmin || isConsultant);

  const hasInteractions =
    mockInteractions[client.id] && mockInteractions[client.id].length > 0;

  // Información de agencia del cliente
  const agencyId = client.agency_id || client.agency || null;
  const clientAgencyName = client.agency_name || null;
  const isArchived = Boolean(client.is_archived);
  const showArchivedDate = isArchived && archivedFilter === "archived";
  const archivedAtLabel = React.useMemo(() => {
    if (!client.archived_at) return null;

    try {
      return new Intl.DateTimeFormat("es-ES", {
        dateStyle: "medium",
      }).format(new Date(client.archived_at));
    } catch (error) {
      return null;
    }
  }, [client.archived_at]);

  // Mapeo de variantes de pack a colores del Badge
  const packColors = {
    ecommerce: "green",
    basic: "orange",
    premium: "purple",
    local: "blue",
    infoproducto: "yellow",
    sin_pack: "destructive",
  };
  const hasSetupProject = Boolean(client.setup_project_info?.id);
  const setupProjectState = client.setup_project_info?.state || null;
  const isSetupCompleted = setupProjectState === "completed";
  const recurringConfigBlockedReason = !hasSetupProject
    ? "Debes tener un proyecto de setup para configurar la recurrencia de proyectos."
    : !isSetupCompleted
      ? "Debes completar el proyecto de setup para configurar la recurrencia de proyectos."
      : null;
  const canOpenRecurringConfig = !recurringConfigBlockedReason;
  const externalLinks = React.useMemo(
    () =>
      [
        {
          key: "website",
          label: "Página web",
          icon: Globe,
          url: client.website,
          iconSize: 13,
        },
        {
          key: "drive_url",
          label: "Drive",
          icon: GoogleDriveIcon,
          url: client.drive_url,
          iconSize: 15,
        },
        {
          key: "notebooklm_url",
          label: "NotebookLM",
          icon: NotebookPen,
          url: client.notebooklm_url,
          iconSize: 13,
        },
        {
          key: "clientify_url",
          label: "Clientify",
          icon: BarChart,
          url: client.clientify_url,
          iconSize: 15,
        },
        {
          key: "todolist_url",
          label: "TodoList",
          icon: ListTodo,
          url: client.todolist_url,
          iconSize: 13,
        },
      ].filter((link) => Boolean(link.url)),
    [
      client.drive_url,
      client.notebooklm_url,
      client.todolist_url,
      client.website,
      client.clientify_url,
    ]
  );
  const hasExternalLinks = externalLinks.length > 0;

  // Usar datos de consultores del cliente
  const consultantDetails = React.useMemo(() => {
    // Priorizar estado local si existe (para actualización optimista)
    if (localConsultantDetails !== null) {
      return localConsultantDetails;
    }

    // Usar consultores expandidos del cliente (para superadmin y agency_admin)
    if (client.consultants && Array.isArray(client.consultants)) {
      return client.consultants;
    }

    // Usar datos cargados bajo demanda
    if (
      clientConsultantsData?.consultants &&
      Array.isArray(clientConsultantsData.consultants)
    ) {
      return clientConsultantsData.consultants;
    }

    return [];
  }, [
    localConsultantDetails,
    client.consultants,
    clientConsultantsData?.consultants,
  ]);

  // Obtener el número de consultores asignados
  const assignedConsultantsCount = consultantDetails.length;

  // Función para obtener el texto de los consultores asignados
  const getConsultantsDisplayText = () => {
    if (assignedConsultantsCount === 0) {
      return "Seleccionar consultores";
    }

    if (assignedConsultantsCount <= 4) {
      // Mostrar nombres separados por comas
      const consultantNames = consultantDetails.map(
        (consultant) =>
          consultant.full_name ||
          `${consultant.first_name || ""} ${consultant.last_name || ""}`.trim()
      );
      return consultantNames.join(", ");
    } else {
      // Mostrar conteo para más de 4 consultores
      return `${assignedConsultantsCount} consultor${assignedConsultantsCount > 1 ? "es" : ""
        } asignado${assignedConsultantsCount > 1 ? "s" : ""}`;
    }
  };

  // Obtener consultores disponibles según el rol
  const getAvailableConsultants = () => {
    if (isSuperAdmin) {
      // Para superadmin, filtrar consultores de la agencia del cliente
      // Incluye consultores cuya agencia primaria es la del cliente O están invitados a esa agencia
      if (agencyId && availableConsultants) {
        return availableConsultants.filter((consultant) => {
          // Check primary agency
          if (consultant.agency === agencyId) return true;

          // Check invited agencies
          if (consultant.invited_agencies && Array.isArray(consultant.invited_agencies)) {
            return consultant.invited_agencies.some(inv => inv.id === agencyId);
          }

          return false;
        });
      }
      // Si no hay agencia del cliente, mostrar todos los consultores
      return availableConsultants || [];
    } else if (isAgencyAdmin) {
      // Para agency_admin, usar todos los consultores disponibles (ya filtrados por su agencia)
      return availableConsultants || [];
    }
    return [];
  };

  const consultantOptions = getAvailableConsultants().map((consultant) => ({
    value: consultant.id,
    label: `${consultant.first_name} ${consultant.last_name}`,
    agencyName: consultant.agency_name || null,
    profile_image: getAvatarImageUrl(consultant.profile_image),
  }));

  const handleConsultantSelectorOpen = async (event) => {
    setConsultantSelectorAnchor(event.currentTarget);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setMenuAnchorEl(null);
  };

  const handleGoToTechnicalSetup = (event) => {
    event.stopPropagation();
    if (setTechnicalSetupRefreshKey) {
      setTechnicalSetupRefreshKey((prev) => prev + 1);
    }
    const projectId = client.setup_project_info?.id;
    if (projectId) {
      navigate(`/setup/${projectId}`);
    } else {
      navigate("/setup");
    }
  };

  const handleGoToConversations = async (event) => {
    event.stopPropagation();

    if (isOpeningConversation) return;
    setIsOpeningConversation(true);
    const main_conversationId = client.main_thread?.id;
    if (main_conversationId) {
      navigate(`/conversations/${client.id}/${main_conversationId}`);
    } else {
      navigate(`/conversations`);
    }
  };

  const handleOpenTeamModal = (event) => {
    event.stopPropagation();
    setTeamRefreshKey((prev) => prev + 1);
    setShowTeamModal(true);
  };

  const handleOpenRecurringConfig = (event) => {
    event.stopPropagation();
    if (!canOpenRecurringConfig) return;
    if (onRecurringConfigClick) {
      onRecurringConfigClick(event, client);
      return;
    }
    setRecurringConfigRefreshKey((prev) => prev + 1);
    setRecurringConfigModalOpen(true);
  };

  const handleCloseRecurringConfig = () => {
    setRecurringConfigModalOpen(false);
  };

  const handleConsultantSelectorClose = () => {
    setConsultantSelectorAnchor(null);
  };

  // Función para eliminar cliente
  const handleDeleteClient = () => {
    setShowDeleteModal(true);
    handleMenuClose();
  };

  const handleToggleArchive = async () => {
    if (isTogglingArchive) return;
    setIsTogglingArchive(true);
    const successMessage = isArchived
      ? "Cliente desarchivado correctamente"
      : "Cliente archivado correctamente";
    const errorMessage = isArchived
      ? "No se pudo desarchivar el cliente"
      : "No se pudo archivar el cliente";

    try {
      const endpoint = isArchived
        ? `clients/${client.id}/unarchive/`
        : `clients/${client.id}/archive/`;
      await apiClient.post(endpoint);
      if (onClientUpdated) onClientUpdated();
      if (refreshFilterData && (isAgencyAdmin || isConsultant))
        refreshFilterData();
      onSuccess(successMessage);
    } catch (error) {
      console.error("Error toggling archive status:", error);
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data;
      onError(
        typeof backendMessage === "string" ? backendMessage : errorMessage
      );
    } finally {
      setIsTogglingArchive(false);
      handleMenuClose();
    }
  };

  // Función para editar foto del cliente
  const handleEditPhoto = () => {
    // Reset local modal state when opening
    setImageMarkedForDeletion(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPhotoError(null);
    setCropMessage(null);
    setShowEditPhotoModal(true);
    handleMenuClose();
  };

  // Función para eliminar la imagen
  const handleDeleteImage = async () => {
    try {
      await apiClient.patch(`clients/${client.id}/`, { logo: null });
      setShowEditPhotoModal(false);
      if (onClientUpdated) onClientUpdated();
      if (onSuccess) onSuccess("Imagen eliminada exitosamente");
    } catch (error) {
      console.error("Error eliminando imagen:", error);
      setPhotoError("Error al eliminar la imagen");
    }
  };

  // Función para seleccionar archivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Selecting a new file cancels any pending deletion mark
    setImageMarkedForDeletion(false);

    if (!file.type.startsWith("image/")) {
      setPhotoError("Por favor selecciona un archivo de imagen válido");
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      // Si la imagen no es cuadrada, la recortamos al tamaño más pequeño
      if (img.width !== img.height) {
        const size = Math.min(img.width, img.height);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = size;
        canvas.height = size;

        // Centrar el recorte
        const x = (img.width - size) / 2;
        const y = (img.height - size) / 2;

        ctx.drawImage(img, x, y, size, size, 0, 0, size, size);

        canvas.toBlob((blob) => {
          const croppedFile = new File([blob], file.name, { type: file.type });
          setSelectedFile(croppedFile);
          setPreviewUrl(URL.createObjectURL(croppedFile));
          setPhotoError(null);
          setCropMessage(
            "Esta imagen no es cuadrada, por lo que ha sido recortada"
          );
        }, file.type);
      } else {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setPhotoError(null);
        setCropMessage(null);
      }
    };
    img.src = URL.createObjectURL(file);
  };

  // Hook para manejar pegado de imágenes desde el portapapeles
  const handlePaste = usePasteSingleImage({
    onImageProcessed: (file, url) => {
      setImageMarkedForDeletion(false);
      setSelectedFile(file);
      setPreviewUrl(url);
      setPhotoError(null);
    },
    onCropMessage: setCropMessage,
    onError: setPhotoError,
  });

  // Escuchar eventos de paste a nivel de documento cuando el modal está abierto
  useEffect(() => {
    if (!showEditPhotoModal) return;

    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [showEditPhotoModal, handlePaste]);

  // Función para subir la imagen
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setPhotoError(null);
    const formData = new FormData();
    formData.append("logo", selectedFile);

    try {
      await apiClient.patch(`clients/${client.id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setShowEditPhotoModal(false);
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      if (onClientUpdated) onClientUpdated();
      if (onSuccess) onSuccess("Foto del cliente actualizada exitosamente");
    } catch (error) {
      console.error("Error subiendo imagen:", error);
      let errorMessage = "Error al subir la imagen";
      if (
        error.response?.data?.logo &&
        Array.isArray(error.response.data.logo)
      ) {
        errorMessage = error.response.data.logo[0];
      } else if (error.response?.data?.logo) {
        errorMessage = error.response.data.logo;
      }
      setPhotoError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Función para confirmar eliminación
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await apiClient.delete(`clients/${client.id}/`);
      onClientDeleted();
      if (refreshFilterData && (isAgencyAdmin || isConsultant))
        refreshFilterData();
      setShowDeleteModal(false);
      if (onSuccess) {
        onSuccess("Cliente eliminado exitosamente");
      }
    } catch (error) {
      console.error("Error eliminando cliente:", error);
      alert("Error al eliminar el cliente. Inténtalo de nuevo.");
    } finally {
      setIsDeleting(false);
    }
  };

  const clientLogo = getAvatarImageUrl(client.logo);

  // Calculated displayed image for the modal (preview takes precedence, deletion mark hides existing logo)
  const displayedImage =
    previewUrl || (clientLogo && !imageMarkedForDeletion ? clientLogo : null);

  // Función para manejar cambios en consultores múltiples
  const handleConsultantToggle = async (consultantId, isChecked) => {
    try {
      // Encontrar el consultor en la lista de consultores disponibles
      const availableConsultantsList = getAvailableConsultants();
      const consultant = availableConsultantsList.find(
        (c) => c.id === consultantId
      );
      if (!consultant) return;

      // Nombre completo del consultor
      const consultantName = consultant.full_name ||
        `${consultant.first_name} ${consultant.last_name}`;

      // Actualización optimista: Actualizar estado local inmediatamente
      const currentConsultants = consultantDetails;
      let newConsultants;

      if (isChecked) {
        // Añadir consultor (convertir formato si es necesario)
        const consultantToAdd = {
          id: consultant.id,
          full_name: consultantName,
          email: consultant.email,
          role: consultant.role || "consultant",
          agency: consultant.agency_name || consultant.agency,
        };
        newConsultants = [...currentConsultants, consultantToAdd];
      } else {
        // Remover consultor
        newConsultants = currentConsultants.filter(
          (c) => c.id !== consultantId
        );
      }

      // Actualizar estado local para mostrar cambio inmediatamente
      setLocalConsultantDetails(newConsultants);

      // Hacer la llamada a la API en segundo plano
      if (isChecked) {
        await apiClient.post(`clients/${client.id}/add-consultant/`, {
          consultant_id: consultantId,
        });
        onSuccess(`Consultor ${consultantName} asignado correctamente al cliente ${client.name}`);
      } else {
        await apiClient.post(`clients/${client.id}/remove-consultant/`, {
          consultant_id: consultantId,
        });
        onSuccess(`Consultor ${consultantName} desasignado correctamente del cliente ${client.name}`);
      }

      // No cerramos el popup para permitir selecciones múltiples
    } catch (error) {
      console.error("Error al modificar el consultor:", error);
      // En caso de error, revertir el cambio optimista
      setLocalConsultantDetails(null);
      onError("Error al modificar el consultor. Inténtalo de nuevo");
    }
  };

  const renderAvatar = (size) => (
              <Tooltip title={isReadOnly ? "Cliente en modo solo lectura" : "Editar imagen del cliente"} arrow>
                <Box
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isReadOnly) handleEditPhoto();
                  }}
                  sx={{
                    cursor: isReadOnly ? "default" : "pointer",
                    position: "relative",
                    flexShrink: 0,
                    transition: "all 0.2s",
                    ...(!isReadOnly && { "&:hover": {
                      "& .avatar-overlay": {
                        opacity: 1,
                      },
                    }}),
                  }}
                >
                  <Avatar
                    sx={{ width: `${size}px`, height: `${size}px` }}
                    src={clientLogo || undefined}
                  >
                    <AvatarFallback
                      sx={{
                        backgroundColor: brand.avatar,
                        color: "#fff",
                        fontSize: size <= 24 ? "0.625rem" : "0.875rem",
                        fontWeight: 600,
                      }}
                    >
                      {client.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  {!isReadOnly && <Box
                    className="avatar-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: "50%",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                  >
                    <svg
                      width={size <= 24 ? 11 : 16}
                      height={size <= 24 ? 11 : 16}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </Box>}
                </Box>
              </Tooltip>
  );

  // Botones de acciones, separados para poder repartirlos en columnas en la vista tabla
  const recurrenceButton = (
    <>
              {canConfigureRecurrence && (
                <Tooltip
                  title={
                    recurringButtonTooltip ||
                    recurringConfigBlockedReason ||
                    "Configurar recurrencia de proyectos"
                  }
                  arrow
                >
                  <span style={{ display: "inline-flex" }}>
                    <IconButton
                      data-tutorial={recurringButtonDataTutorial}
                      size="small"
                      onClick={handleOpenRecurringConfig}
                      disabled={!canOpenRecurringConfig}
                      sx={{
                        width: "32px",
                        height: "32px",
                        padding: 0,
                        flexShrink: 0,
                        opacity: canOpenRecurringConfig ? 1 : 0.65,
                        ...(highlightRecurringButton
                          ? {
                            position: "relative",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              inset: "-3px",
                              borderRadius: "50%",
                              border: `1px solid ${theme.palette.error.main}`,
                              pointerEvents: "none",
                            },
                          }
                          : {}),
                      }}
                    >
                      <Repeat size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
    </>
  );

  // Accesos a otras secciones de la plataforma (setup, conversación, equipo)
  const platformLinks = (
    <>
              <Tooltip
                title={
                  hasSetupProject
                    ? "Ver Setup técnico"
                    : "Contrata un pack para acceder al setup. Al contratar un pack, automáticamente se crea un proyecto de setup para este cliente"
                }
                arrow
              >
                <span style={{ display: "inline-flex" }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      if (!hasSetupProject) {
                        e.stopPropagation();
                        return;
                      }
                      handleGoToTechnicalSetup(e);
                    }}
                    disabled={!hasSetupProject}
                    sx={{
                      width: "32px",
                      height: "32px",
                      padding: 0,
                      flexShrink: 0,
                      opacity: hasSetupProject ? 1 : 0.65,
                    }}
                  >
                    <SetupIcon size={16} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Ver conversación principal" arrow>
                <IconButton
                  size="small"
                  onClick={handleGoToConversations}
                  disabled={isOpeningConversation}
                  sx={{
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    flexShrink: 0,
                    opacity: isOpeningConversation ? 0.6 : 1,
                  }}
                >
                  <MessageSquare size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Ver equipo" arrow>
                <IconButton
                  size="small"
                  onClick={handleOpenTeamModal}
                  sx={{
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  <UserCog size={16} />
                </IconButton>
              </Tooltip>
    </>
  );

  const moreActionsMenu = (
    <>
              <Tooltip title={isReadOnly ? "Cliente en modo solo lectura" : "Ver otras acciones"} arrow>
                <IconButton
                  size="small"
                  disabled={isReadOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e);
                  }}
                  sx={{
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  <MoreVertical size={16} />
                </IconButton>
              </Tooltip>

              <DropdownMenuContent
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                {/* En la tabla la recurrencia va en el menú; en la tarjeta tiene su propio botón */}
                {layout === "row" && canConfigureRecurrence && (
                  <DropdownMenuItem
                    onClick={handleOpenRecurringConfig}
                    disabled={!canOpenRecurringConfig}
                    title={recurringConfigBlockedReason || undefined}
                  >
                    <Repeat size={16} />
                    Configurar recurrencia
                  </DropdownMenuItem>
                )}
                {isAnyAdmin && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPhoto();
                    }}
                  >
                    <ImageIcon size={16} />
                    Editar foto del cliente
                  </DropdownMenuItem>
                )}
                {(isSuperAdmin || isAgencyAdmin) && !isReadOnly && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleArchive();
                    }}
                    disabled={isTogglingArchive}
                  >
                    {isArchived ? (
                      <ArchiveRestore size={16} />
                    ) : (
                      <Archive size={16} />
                    )}
                    {isArchived ? "Desarchivar" : "Archivar"} cliente
                  </DropdownMenuItem>
                )}
                {isSuperAdmin && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClient();
                    }}
                    sx={{ color: theme.palette.error.main }}
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
    </>
  );

  const actionButtons = (
    <>
      {recurrenceButton}
      {platformLinks}
      {moreActionsMenu}
    </>
  );

  // Enlaces externos del cliente: solo icono, en azul, con el nombre en el tooltip
  const externalLinkIcons = hasExternalLinks ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
      {externalLinks.map(({ key, label, icon: Icon, url, iconSize }) => (
        <Tooltip key={key} title={label} arrow>
          <IconButton
            size="small"
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            sx={{
              width: "32px",
              height: "32px",
              padding: 0,
              color: "#1976d2",
              "&:hover": { color: "#1565c0", backgroundColor: "rgba(25, 118, 210, 0.08)" },
            }}
          >
            <Icon size={iconSize + 2} sx={{ fontSize: iconSize + 2 }} />
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  ) : (
    <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>-</Typography>
  );

  const manageConsultantsPopover = (
                                        <Popover
                      open={consultantSelectorOpen}
                      anchorEl={consultantSelectorAnchor}
                      onClose={handleConsultantSelectorClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      PaperProps={{
                        sx: {
                          width: consultantSelectorAnchor
                            ? consultantSelectorAnchor.offsetWidth
                            : "auto",
                          minWidth: 240,
                          maxHeight: "170px",
                          boxShadow: theme.shadows[8],
                          border: `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <Box sx={{ padding: "8px" }}>
                        {loadingConsultants ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              padding: "16px",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                color: theme.palette.text.secondary,
                              }}
                            >
                              Cargando consultores...
                            </Typography>
                          </Box>
                        ) : consultantOptions.length > 0 ? (
                          <Stack spacing={0.5}>
                            {consultantOptions.map((consultant) => {
                              const isAssigned = consultantDetails.some(
                                (c) => c.id === consultant.value
                              );

                              return (
                                <Box
                                  key={consultant.value}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    padding: "6px 8px",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                  }}
                                  onClick={() => {
                                    handleConsultantToggle(
                                      consultant.value,
                                      !isAssigned
                                    );
                                  }}
                                >
                                  <Checkbox
                                    checked={isAssigned}
                                    onChange={() => { }} // Manejado por el onClick del Box
                                  />
                                  <Avatar
                                    sx={{ width: "24px", height: "24px" }}
                                    src={
                                      getAvatarImageUrl(
                                        consultant.profile_image
                                      ) || undefined
                                    }
                                  >
                                    <AvatarFallback
                                      sx={{
                                        backgroundColor:
                                          brand.avatar,
                                        color: "#fff",
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {(consultant.label || "C")
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {consultant.label}
                                    </Typography>
                                    {selectedFilter === null &&
                                      consultant.agencyName && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.65rem",
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {consultant.agencyName}
                                        </Typography>
                                      )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              color: theme.palette.text.secondary,
                              padding: "8px",
                              textAlign: "center",
                            }}
                          >
                            No hay consultores disponibles
                          </Typography>
                        )}
                      </Box>
                    </Popover>
  );

  const readonlyConsultantsPopover = (
                                <Popover
                  open={consultantSelectorOpen}
                  anchorEl={consultantSelectorAnchor}
                  onClose={handleConsultantSelectorClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  PaperProps={{
                    sx: {
                      width: consultantSelectorAnchor
                        ? consultantSelectorAnchor.offsetWidth
                        : "auto",
                      minWidth: 220,
                      maxHeight: "180px",
                      boxShadow: theme.shadows[8],
                      border: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <Box sx={{ padding: "8px" }}>
                    {consultantDetails.length > 0 ? (
                      <Stack spacing={0.5}>
                        {consultantDetails.map((consultant, index) => (
                          <Box
                            key={consultant.id || index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              padding: "6px 8px",
                              borderRadius: "4px",
                              backgroundColor: theme.palette.action.hover,
                            }}
                          >
                            <Avatar
                              sx={{ width: "24px", height: "24px" }}
                              src={
                                getAvatarImageUrl(consultant.profile_image) ||
                                undefined
                              }
                            >
                              <AvatarFallback
                                sx={{
                                  backgroundColor: brand.avatar,
                                  color: "#fff",
                                  fontSize: "0.65rem",
                                  fontWeight: 600,
                                }}
                              >
                                {(
                                  consultant.full_name ||
                                  `${consultant.first_name || ""} ${consultant.last_name || ""
                                  }`
                                )
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
                              >
                                {consultant.full_name ||
                                  `${consultant.first_name || ""} ${consultant.last_name || ""
                                    }`.trim()}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: theme.palette.text.secondary,
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        No hay consultores asignados
                      </Typography>
                    )}
                  </Box>
                </Popover>
  );

  const cardView = (
      <Card
        sx={{
          width: "100%",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <CardContent sx={{ padding: "10px", width: "100%" }}>
          {/* Header compacto */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: "16px",
              width: "100%",
              // En móvil las acciones bajan a su propia fila para que el
              // nombre del cliente no quede aplastado.
              flexWrap: { xs: "wrap", sm: "nowrap" },
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: { xs: "1 1 100%", sm: 1 },
                minWidth: 0,
              }}
            >
              {renderAvatar(40)}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    rowGap: "4px",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    marginBottom: "2px",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {client.name}
                  </Typography>

                  {/* Badge de Pack */}
                  {showPack && (
                  <Badge
                    size="sm"
                    color={
                      packColors[
                      client.setup_project_info?.setup_variant || "sin_pack"
                      ]
                    }
                    sx={
                      !client.setup_project_info?.setup_variant
                        ? {
                          backgroundColor: "transparent",
                          color: "text.secondary",
                          border: `1px solid ${theme.palette.divider}`,
                        }
                        : undefined
                    }
                  >
                    {
                      packLabels[
                      client.setup_project_info?.setup_variant || "sin_pack"
                      ]
                    }
                  </Badge>
                  )}
                  {isArchived && (
                    <Badge
                      size="sm"
                      sx={{
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.common.white,
                        border: `1px solid ${theme.palette.error.main}`,
                      }}
                    >
                      Archivado
                    </Badge>
                  )}
                  {isReadOnly && <ReadOnlyBadge />}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "0.875rem",
                    color: theme.palette.text.secondary,
                    minWidth: 0,
                  }}
                >
                  {client.email ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}
                    >
                      <Mail size={12} />
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {client.email}
                      </Typography>
                    </Box>
                  ) : client.contact_name ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}
                    >
                      <User size={12} />
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {client.contact_name}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexShrink: 0,
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "flex-end", sm: "flex-start" },
              }}
            >
              {actionButtons}
            </Box>
          </Box>

          {/* Asignaciones compactas - Siempre visible */}
          <Stack spacing={1.5}>
            {/* Agencia - Solo visualización */}
            <Box>
              <Label
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.text.secondary,
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Agencia
              </Label>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  padding: "8px 12px",
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  color: theme.palette.text.primary,
                }}
              >
                {clientAgencyName || "Sin agencia asignada"}
              </Typography>
            </Box>

            {/* Consultores - Solo Super Admin y Agency Admin pueden cambiar */}
            {canManageConsultants && (
              <Box>
                <Label
                  sx={{
                    fontSize: "0.75rem",
                    color: theme.palette.text.secondary,
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Consultores asignados
                </Label>
                {consultantOptions.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      color: theme.palette.text.secondary,
                      backgroundColor: theme.palette.action.hover,
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  >
                    No hay consultores disponibles
                  </Typography>
                ) : (
                  <>
                    {/* Botón del multiselector */}
                    <Tooltip title="Asignar consultores" arrow>
                      <Button
                        variant="outline"
                        onClick={handleConsultantSelectorOpen}
                        sx={{
                          width: "100%",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          fontSize: "0.75rem",
                          height: "auto",
                          minHeight: "32px",
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Users size={14} />
                          <Typography sx={{ fontSize: "0.75rem" }}>
                            {loadingConsultants
                              ? "Cargando..."
                              : getConsultantsDisplayText()}
                          </Typography>
                        </Box>
                        {consultantSelectorOpen ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </Button>
                    </Tooltip>
                    {manageConsultantsPopover}
                  </>
                )}
              </Box>
            )}

            {showArchivedDate && archivedAtLabel && (
              <Box>
                <Label
                  sx={{
                    fontSize: "0.75rem",
                    color: theme.palette.text.secondary,
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Fecha de baja
                </Label>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    padding: "8px 12px",
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    color: theme.palette.text.primary,
                  }}
                >
                  {archivedAtLabel}
                </Typography>
              </Box>
            )}

            {/* Vista para Consultores - Solo visualización */}
            {isConsultant && (
              <Box>
                <Label
                  sx={{
                    fontSize: "0.75rem",
                    color: theme.palette.text.secondary,
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Consultores asignados
                </Label>

                {/* Botón del selector (solo lectura) */}
                <Button
                  variant="outline"
                  onClick={handleConsultantSelectorOpen}
                  sx={{
                    width: "100%",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    fontSize: "0.75rem",
                    height: "auto",
                    minHeight: "32px",
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Users size={14} />
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {assignedConsultantsCount > 0
                        ? getConsultantsDisplayText()
                        : "Sin consultores asignados"}
                    </Typography>
                  </Box>
                  {consultantSelectorOpen ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </Button>

                {readonlyConsultantsPopover}
              </Box>
            )}

            {/* Botón de acciones */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                marginTop: 2,
                alignItems: "center",
                justifyContent: {
                  xs: "flex-end",
                  sm: hasExternalLinks ? "space-between" : "flex-end",
                },
                flexWrap: "nowrap",
              }}
            >
              {hasExternalLinks && (
                // Los enlaces externos se ocultan en móvil: no caben junto al
                // botón y siguen disponibles en la ficha del cliente.
                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    gap: 1.5,
                    flexWrap: "nowrap",
                    alignItems: "center",
                    overflow: "hidden",
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  {externalLinks.map(
                    ({ key, label, icon: Icon, url, iconSize }) => (
                      <Button
                        key={key}
                        variant="link"
                        size="sm"
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.75rem",
                          padding: 0,
                          minWidth: "auto",
                          width: "auto",
                          color: "#1976d2",
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          flexShrink: 1,
                          "&:hover": {
                            color: "#1565c0",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.text.secondary,
                            display: "flex",
                          }}
                        >
                          <Icon size={iconSize} sx={{ fontSize: iconSize }} />
                        </Box>
                        {label}
                      </Button>
                    )
                  )}
                </Box>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onViewOnboarding) onViewOnboarding(client.id);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.75rem",
                  flexShrink: 0,
                }}
              >
                <FileText size={14} />
                Ver detalles
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
  );

  const setupState = client.setup_project_info?.state || null;
  const setupProgress = Math.round(client.setup_project_info?.progress_percentage || 0);
  const createdAtLabel = React.useMemo(() => {
    if (!client.created_at) return null;
    try {
      return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(
        new Date(client.created_at)
      );
    } catch {
      return null;
    }
  }, [client.created_at]);

  const packVariant = client.setup_project_info?.setup_variant || "sin_pack";
  const packBadge = (
    <Badge
      size="sm"
      color={packColors[packVariant]}
      sx={
        !client.setup_project_info?.setup_variant
          ? {
            backgroundColor: "transparent",
            color: "text.secondary",
            border: `1px solid ${theme.palette.divider}`,
          }
          : undefined
      }
    >
      {packLabels[packVariant]}
    </Badge>
  );

  const renderRowCell = (key) => {
    switch (key) {
      case "client":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {/* Mismo tamaño que el avatar "sm" del resto de tablas */}
            {renderAvatar(24)}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 260,
                  }}
                >
                  {client.name}
                </Typography>
                {isArchived && (
                  <Badge
                    size="sm"
                    sx={{
                      backgroundColor: theme.palette.error.main,
                      color: theme.palette.common.white,
                      border: `1px solid ${theme.palette.error.main}`,
                    }}
                  >
                    Archivado
                  </Badge>
                )}
                {isReadOnly && <ReadOnlyBadge />}
              </Box>
              {(client.email || client.contact_name) && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 260,
                  }}
                >
                  {client.email || client.contact_name}
                </Typography>
              )}
            </Box>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewOnboarding) onViewOnboarding(client.id);
              }}
              sx={{ ml: "auto", flexShrink: 0, gap: 0.5, fontSize: "0.75rem", whiteSpace: "nowrap" }}
            >
              <FileText size={14} />
              Ver detalles
            </Button>
          </Box>
        );
      case "pack":
        return packBadge;
      case "agency":
        return clientAgencyName || "Sin agencia";
      case "consultants":
        if (canManageConsultants && consultantOptions.length === 0 && assignedConsultantsCount === 0) {
          return (
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Sin consultores disponibles
            </Typography>
          );
        }
        return (
          <>
            <Tooltip title={canManageConsultants ? "Asignar consultores" : "Ver consultores"} arrow>
              <Box
                component="button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleConsultantSelectorOpen(event);
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  maxWidth: 240,
                  px: 1,
                  py: 0.5,
                  ml: -1,
                  border: "none",
                  borderRadius: "8px",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "0.8125rem",
                  color: assignedConsultantsCount > 0 ? "text.primary" : "text.secondary",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <Users size={14} style={{ flexShrink: 0 }} />
                <Box
                  component="span"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {assignedConsultantsCount > 0
                    ? getConsultantsDisplayText()
                    : canManageConsultants
                      ? "Asignar"
                      : "Sin consultores"}
                </Box>
                {consultantSelectorOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </Box>
            </Tooltip>
            {canManageConsultants ? manageConsultantsPopover : readonlyConsultantsPopover}
          </>
        );
      case "setup":
        if (!hasSetupProject) {
          return (
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Sin setup
            </Typography>
          );
        }
        return (
          <SetupProgress value={setupProgress} completed={setupState === "completed"} />
        );
      case "created":
        return createdAtLabel || "-";
      case "archived":
        return archivedAtLabel || "-";
      case "actions":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
            {moreActionsMenu}
          </Box>
        );
      case "platform":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
            {platformLinks}
          </Box>
        );
      case "external":
        return externalLinkIcons;
      default:
        return null;
    }
  };

  // Fila de la vista tabla: la fila entera abre la ficha del cliente. Los clicks que
  // llegan desde modales/popovers (portales) se ignoran para no navegar por error.
  const rowView = (
    <TableRow
      hover
      onClick={(event) => {
        if (!event.currentTarget.contains(event.target)) return;
        if (onViewOnboarding) onViewOnboarding(client.id);
      }}
      sx={{ cursor: "pointer" }}
    >
      {(columns || []).map((column) => (
        <TableCell key={column.key} align={column.align || "left"}>
          {renderRowCell(column.key)}
        </TableCell>
      ))}
    </TableRow>
  );

  const modals = (
    <>
      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title={`Eliminar cliente: ${client.name}`}
        question="¿Estás seguro de que quieres eliminar este cliente?"
        description={
          <>
            Esta acción no se puede deshacer. Se eliminará permanentemente el cliente{" "}
            <strong>"{client.name}"</strong> y toda su información asociada.
          </>
        }
        confirmLabel="Eliminar cliente"
        loadingLabel="Eliminando..."
        loading={isDeleting}
        tone="danger"
      />

      {/* Modal para editar foto del cliente */}
      <UnifiedModal
        open={showEditPhotoModal}
        onClose={() => {
          setShowEditPhotoModal(false);
          setSelectedFile(null);
          setPhotoError(null);
          setCropMessage(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
        title={
          clientLogo ? "Editar imagen del cliente" : "Nueva imagen del cliente"
        }
        maxWidth="xs"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {displayedImage ? (
              <Box sx={{ position: "relative" }}>
                <Avatar
                  sx={{ width: "80px", height: "80px" }}
                  src={getAvatarImageUrl(displayedImage) || undefined}
                >
                  <AvatarFallback
                    sx={{
                      backgroundColor: brand.avatar,
                      color: "#fff",
                      fontSize: "2rem",
                      fontWeight: 600,
                    }}
                  >
                    {client.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    // mark for deletion in UI only; actual API delete happens on Confirm
                    setImageMarkedForDeletion(true);
                    // clear any selected file/preview
                    setSelectedFile(null);
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl);
                      setPreviewUrl(null);
                    }
                    setPhotoError(null);
                  }}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    width: 28,
                    height: 28,
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <X size={14} />
                </IconButton>
              </Box>
            ) : (
              <Box
                onClick={() => fileInputRef.current.click()}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    border: `1px dashed ${theme.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Plus size={28} color={theme.palette.text.secondary} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: theme.palette.text.secondary }}
                >
                  Añadir imagen
                </Typography>
              </Box>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: theme.palette.secondary.contrastText,
            }}
          >
            {clientLogo
              ? `Edita la imagen del cliente: ${client.name}`
              : `Añade una nueva imagen al cliente: ${client.name}`}
          </Typography>
          {photoError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center" }}
            >
              {photoError}
            </Typography>
          )}
          {cropMessage && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center" }}
            >
              {cropMessage}
            </Typography>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: "auto",
            }}
          >
            <Button
              onClick={() => {
                // Cancel: revert local changes and close modal
                setShowEditPhotoModal(false);
                setSelectedFile(null);
                setPhotoError(null);
                setCropMessage(null);
                setImageMarkedForDeletion(false);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
              }}
              disabled={uploading}
              variant="outline"
            >
              Cancelar
            </Button>

            <Button
              onClick={async () => {
                // Confirm: perform delete if marked, otherwise upload if a file is selected
                if (imageMarkedForDeletion) {
                  await handleDeleteImage();
                } else if (selectedFile) {
                  await handleUpload();
                } else {
                  // Nothing to do, just close modal
                  setShowEditPhotoModal(false);
                  setImageMarkedForDeletion(false);
                }
                // After action, cleanup local state
                setSelectedFile(null);
                setPhotoError(null);
                setCropMessage(null);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
                setImageMarkedForDeletion(false);
              }}
              disabled={uploading}
            >
              {uploading ? "Procesando..." : "Confirmar"}
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Modal de equipo */}
      <MembersModal
        open={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        title={`Equipo de ${client.name}`}
        isLoading={loadingTeam}
        participants={[
          ...teamMembers.admins.map((admin) => ({
            id: admin.id,
            full_name: admin.full_name || `${admin.first_name} ${admin.last_name}`,
            email: admin.email,
            role: admin.role,
            profile_image: getAvatarImageUrl(admin.profile_image),
          })),
          ...teamMembers.consultants.map((consultant) => ({
            id: consultant.id,
            full_name: consultant.full_name,
            email: consultant.email,
            role: consultant.role || "consultant",
            profile_image: getAvatarImageUrl(consultant.profile_image),
          })),
          ...teamMembers.clientUsers.map((clientUser) => ({
            id: clientUser.id,
            full_name: clientUser.full_name || `${clientUser.first_name} ${clientUser.last_name}`,
            email: clientUser.email,
            role: clientUser.role,
            profile_image: getAvatarImageUrl(clientUser.profile_image),
          })),
        ]}
        clientName={client.name}
        agencyName={client.agency_name || ""}
      />

      <RecurringProjectConfigModal
        open={recurringConfigModalOpen}
        onClose={handleCloseRecurringConfig}
        clientId={client.id}
        clientName={client.name}
        refreshKey={recurringConfigRefreshKey}
        appRefreshKey={clientsRefreshKey}
        onSaved={(subscription) => {
          const statusLabel =
            subscription?.status === "active" ? "Activado" : "No activado";
          onSuccess(`Recurrencia actualizada: ${statusLabel}`);
        }}
        onError={() => {
          onError("No se pudo actualizar la recurrencia.");
        }}
      />
    </>
  );

  return (
    <>
      {layout === "row" ? rowView : cardView}
      {modals}

    </>
  );
}

export const CompactClientCard = React.memo(CompactClientCardBase);
